/**
 * Ad channel connections.
 *
 * The flow here mirrors what the backend actually implements, not what we'd
 * like it to. Until the app clears platform review, connection means the
 * merchant generates a system-user token inside their own ad account and pastes
 * it here — we're acting on a credential they issued for themselves. Full OAuth
 * lands post-review.
 *
 * The roadmap channels are shown but explicitly not connectable. Showing a
 * "Connect" button for a platform with no adapter behind it would be a lie the
 * merchant only discovers after their campaign silently fails to launch.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';

/** Credential shapes retained for future releases; Meta alone is launch-ready. */
type LivePlatform = 'meta' | 'google' | 'tiktok' | 'reddit' | 'pinterest';

interface ChannelDef {
  id: LivePlatform;
  name: string;
  /** What the merchant gets that they couldn't get running it manually. */
  edge: string;
  /** Where in the platform's UI the token lives. */
  tokenHint: string;
  fields: Array<{
    key: string;
    label: string;
    secret?: boolean;
    required?: boolean;
    placeholder?: string;
  }>;
}

const CONFIGURED: ChannelDef[] = [
  {
    id: 'meta',
    name: 'Meta',
    edge: 'Fast-iteration optimiser — kills a losing creative in 3 days, not 3 weeks.',
    tokenHint: 'Business Manager → System Users → Generate Token (needs ads_management)',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', placeholder: 'act_…', required: true },
      { key: 'page_id', label: 'Facebook Page ID', required: true },
      { key: 'pixel_id', label: 'Meta Pixel ID', required: true },
      { key: 'access_token', label: 'System-user access token', secret: true, required: true },
    ],
  },
  {
    id: 'google',
    name: 'Google Ads',
    edge: 'Performance Max bidding driven by your true incremental ROAS, not last-touch.',
    tokenHint: 'Google Ads API Center → developer token + OAuth refresh token',
    fields: [
      { key: 'customer_id', label: 'Customer ID', placeholder: '0000000000', required: true },
      { key: 'login_customer_id', label: 'Manager customer ID (optional)' },
      { key: 'merchant_center_id', label: 'Merchant Center ID (optional)' },
      { key: 'refresh_token', label: 'OAuth refresh token', secret: true, required: true },
    ],
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    edge: 'Hooks rewritten for a 1.5-second attention window.',
    tokenHint: 'TikTok for Business → Developers → your app → access token',
    fields: [
      { key: 'advertiser_id', label: 'Advertiser ID', required: true },
      { key: 'identity_id', label: 'TikTok identity ID (optional)' },
      { key: 'pixel_code', label: 'TikTok pixel code (optional)' },
      { key: 'access_token', label: 'Access token', secret: true, required: true },
    ],
  },
  {
    id: 'pinterest',
    name: 'Pinterest',
    edge: 'Patient optimiser — Pinterest converts on a slow burn, so we wait longer before judging a creative.',
    tokenHint: 'Pinterest Business → Apps → generate access token',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', required: true },
      { key: 'board_id', label: 'Board ID', required: true },
      { key: 'access_token', label: 'Access token', secret: true, required: true },
    ],
  },
  {
    id: 'reddit',
    name: 'Reddit',
    edge: 'Subreddit language mining plus a tone linter. Paid ads only — we never post organically, because that gets brands permanently banned.',
    tokenHint: 'Reddit Ads → Settings → API access',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', placeholder: 't2_…', required: true },
      { key: 'profile_id', label: 'Profile ID (optional)' },
      { key: 'pixel_id', label: 'Reddit Pixel ID (optional)' },
      { key: 'access_token', label: 'Access token', secret: true, required: true },
    ],
  },
];

const LIVE = CONFIGURED.filter((channel) => channel.id === 'meta');

const ROADMAP = [
  { name: 'Google Ads', why: 'Integration is being production-tested and is not enabled for campaign writes.' },
  { name: 'TikTok', why: 'Integration is being production-tested and is not enabled for campaign writes.' },
  { name: 'Pinterest', why: 'Integration is being production-tested and is not enabled for campaign writes.' },
  { name: 'Reddit Ads', why: 'Intelligence remains available; paid campaign writes are not enabled.' },
  { name: 'Snapchat', why: 'Cheap reach for under-25 audiences your Meta CPMs price you out of.' },
  { name: 'YouTube Shorts', why: 'Reuses your TikTok cuts with no extra production.' },
  { name: 'X', why: 'Cheapest CPMs of the majors — worth testing, rarely worth scaling.' },
  { name: 'Spotify', why: 'Audio hooks generated from your best-performing written hooks.' },
  { name: 'Amazon Ads', why: 'Only worth it if you also sell on Amazon.' },
  { name: 'LinkedIn', why: 'B2B only. High CPMs, poor fit for most Shopify stores.' },
];

type Conn = { platform: string; status: string; connected_at: number };

export default function Channels() {
  const [conns, setConns] = useState<Conn[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<LivePlatform | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .connections()
      .then(setConns)
      .catch(() => setConns([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const active = (id: string) => conns.find((c) => c.platform === id && c.status === 'active');

  async function disconnect(platform: string) {
    setBusy(platform);
    setError(null);
    try {
      await api.disconnectPlatform(platform);
      setConns((c) => c.filter((x) => x.platform !== platform));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not disconnect.');
    } finally {
      setBusy(null);
    }
  }

  async function connect(
    platform: LivePlatform,
    externalId: string,
    credentials: Record<string, string>,
  ) {
    setBusy(platform);
    setError(null);
    try {
      await api.connectPlatform({ platform, externalId, credentials });
      setOpenForm(null);
      setConns((c) => [
        ...c.filter((x) => x.platform !== platform),
        { platform, status: 'active', connected_at: Date.now() },
      ]);
      setToast(`${platform} connected`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not connect that account.');
    } finally {
      setBusy(null);
    }
  }

  async function resync() {
    setResyncing(true);
    try {
      await api.resync();
      setToast('Product resync queued');
      setTimeout(() => setToast(null), 3000);
    } catch {
      setError('Resync could not be queued.');
    } finally {
      setResyncing(false);
    }
  }

  const connectedCount = LIVE.filter((c) => active(c.id)).length;

  return (
    <>
      <p className="eyebrow">Setup</p>
      <h1 className="pg-title">Ad channels</h1>
      <p className="pg-sub">
        Connecting Meta lets ScaleAds AI create paused campaigns, then pause and re-budget approved campaigns. Every
        automated change is logged, capped at ±30% per step, and reversible for 24 hours. Disconnect
        stops all automated spend immediately.
      </p>

      <div className="row gap-2 mb-3">
        <span className="badge b-active">
          {connectedCount} of {LIVE.length} connected
        </span>
        <button className="btn sm" onClick={() => void resync()} disabled={resyncing}>
          {resyncing ? 'Queuing…' : 'Resync Shopify products'}
        </button>
      </div>

      {error && <p className="err-line">{error}</p>}

      <h4 className="tier-head">
        Available now<span className="meta">Meta campaign management</span>
      </h4>

      {loading ? (
        <div className="empty">Loading connections…</div>
      ) : (
        <div className="channel-grid">
          {LIVE.map((c) => {
            const conn = active(c.id);
            return (
              <article key={c.id} className={`card card-p channel ${conn ? 'on' : ''}`}>
                <div className="channel-head">
                  <h3>{c.name}</h3>
                  <span className={`badge ${conn ? 'b-active' : ''}`}>
                    {conn ? 'connected' : 'not connected'}
                  </span>
                </div>
                <p className="channel-edge">{c.edge}</p>

                {openForm === c.id ? (
                  <ConnectForm
                    def={c}
                    busy={busy === c.id}
                    onCancel={() => setOpenForm(null)}
                    onSubmit={(id, credentials) => void connect(c.id, id, credentials)}
                  />
                ) : (
                  <div className="channel-foot">
                    {conn ? (
                      <>
                        <span className="meta">
                          since {new Date(conn.connected_at).toLocaleDateString('en-AU')}
                        </span>
                        <button
                          className="btn sm danger"
                          onClick={() => void disconnect(c.id)}
                          disabled={busy === c.id}
                        >
                          {busy === c.id ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      </>
                    ) : (
                      <button className="btn sm brand" onClick={() => setOpenForm(c.id)}>
                        Connect {c.name}
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <h4 className="tier-head">
        On the roadmap<span className="meta">no adapter yet — not connectable</span>
      </h4>
      <div className="channel-grid">
        {ROADMAP.map((r) => (
          <article key={r.name} className="card card-p channel roadmap">
            <div className="channel-head">
              <h3>{r.name}</h3>
              <span className="badge">planned</span>
            </div>
            <p className="channel-edge">{r.why}</p>
          </article>
        ))}
      </div>

      <p className="footnote">
        Tokens are encrypted at rest with AES-256-GCM and never returned by the API — not even to
        you. If you rotate a token in the platform, reconnect here. Campaigns already live keep
        running under the platform's own settings after you disconnect; we don't delete them,
        because deleting your ad history isn't ours to do.
      </p>

      {toast && <div className="toast show">{toast}</div>}
    </>
  );
}

function ConnectForm({
  def,
  busy,
  onCancel,
  onSubmit,
}: {
  def: ChannelDef;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (externalId: string, credentials: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const requiredComplete = def.fields
    .filter((field) => field.required)
    .every((field) => values[field.key]?.trim());

  return (
    <form
      className="connect-form"
      onSubmit={(e) => {
        e.preventDefault();
        const credentials = Object.fromEntries(
          Object.entries(values)
            .map(([key, value]) => [key, value.trim()])
            .filter(([, value]) => value),
        );
        const identifier =
          credentials.ad_account_id ?? credentials.customer_id ?? credentials.advertiser_id;
        if (!identifier) return;
        onSubmit(identifier, credentials);
      }}
    >
      {def.fields.map((field) => (
        <label className="field" key={field.key}>
          <span>{field.label}</span>
          <input
            className="input"
            type={field.secret ? 'password' : 'text'}
            value={values[field.key] ?? ''}
            placeholder={field.placeholder}
            onChange={(e) => setValues((current) => ({ ...current, [field.key]: e.target.value }))}
            autoComplete="off"
            required={field.required}
          />
        </label>
      ))}
      <p className="footnote" style={{ marginTop: 0 }}>
        {def.tokenHint}
      </p>
      <div className="row gap-2">
        <button type="submit" className="btn sm brand" disabled={busy || !requiredComplete}>
          {busy ? 'Verifying…' : 'Connect'}
        </button>
        <button type="button" className="btn sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
